import axios from "axios";
import { useState } from "react";
import { __API_URL__ } from "../../SERVER_URL";
import { toast } from "react-toastify";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { formatHoursDuration, parseHoursInput } from "../../utils/helpers";

const HOURS_HINT = "Decimals allowed, e.g. 1.5 = 1h 30m";

const AddTrialNotification = ({
  token,
  getTrialsNotification,
  getAdverseNotification,
  getEdiaryNotification,
  hasEdiary = false,
  trialId,
  open,
  onClose,
}) => {
  const [noti, setNoti] = useState("");
  const [title, setTitle] = useState("");
  const [customData, setCustomData] = useState("");
  const [isEmail, setIsEmail] = useState(false);
  const [isApp, setIsApp] = useState(false);
  const [isSms, setIsSms] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [frequency, setFrequency] = useState("");
  const [startDateOffset, setStartDateOffset] = useState(0);
  const [endDateOffset, setEndDateOffset] = useState(0);
  const [useOffset, setUseOffset] = useState(false);

  const [afterType, setAfterType] = useState("Non-Recurrent");

  const [beforeHourInput, setBeforeHourInput] = useState("");
  const [beforeHours, setBeforeHours] = useState([]);

  const [afterHourInput, setAfterHourInput] = useState("");
  const [afterHours, setAfterHours] = useState([]);

  const [afterRecurring, setAfterRecurring] = useState("");

  const [showDe, setShowDe] = useState(false);
  const [customTitleDe, setCustomTitleDe] = useState("");
  const [customDataDe, setCustomDataDe] = useState("");
  const [showEs, setShowEs] = useState(false);
  const [customTitleEs, setCustomTitleEs] = useState("");
  const [customDataEs, setCustomDataEs] = useState("");

  const addHours = (input, hours, setHours, setInput) => {
    const val = parseHoursInput(input);
    if (val === null) {
      toast.error("Enter hours greater than 0 with at most 2 decimals");
      return;
    }
    if (!hours.includes(val)) {
      setHours([...hours, val]);
    }
    setInput("");
  };

  const handleAddBeforeHour = () =>
    addHours(beforeHourInput, beforeHours, setBeforeHours, setBeforeHourInput);

  const handleAddAfterHour = () =>
    addHours(afterHourInput, afterHours, setAfterHours, setAfterHourInput);

  const removeBeforeHour = (index) => {
    const updated = [...beforeHours];
    updated.splice(index, 1);
    setBeforeHours(updated);
  };

  const removeAfterHour = (index) => {
    const updated = [...afterHours];
    updated.splice(index, 1);
    setAfterHours(updated);
  };

  function resetState() {
    setNoti("");
    setTitle("");
    setCustomData("");
    setIsEmail(false);
    setIsApp(false);
    setIsSms(false);
    setStartDate("");
    setEndDate("");
    setFrequency("");
    setStartDateOffset(0);
    setEndDateOffset(0);
    setUseOffset(false);
    setAfterType("Non-Recurrent");
    setBeforeHourInput("");
    setBeforeHours([]);
    setAfterHourInput("");
    setAfterHours([]);
    setAfterRecurring("");
    setShowDe(false);
    setCustomTitleDe("");
    setCustomDataDe("");
    setShowEs(false);
    setCustomTitleEs("");
    setCustomDataEs("");
  }

  const handleClose = () => {
    resetState();
    onClose();
  };

  const validateHours = () => {
    const isAdverse = noti === "ADVERSE_EVENT_NOTIFICATION";
    if (
      !isAdverse &&
      afterType === "Recurrent" &&
      afterRecurring !== "" &&
      parseHoursInput(afterRecurring) === null
    ) {
      return "Recurring hours must be greater than 0 with at most 2 decimals";
    }
    if (isAdverse && frequency !== "" && parseHoursInput(frequency) === null) {
      return "Frequency must be greater than 0 with at most 2 decimals";
    }
    if (
      isAdverse &&
      useOffset &&
      [startDateOffset, endDateOffset].some(
        (offset) => parseHoursInput(offset, { allowZeroOrNegative: true }) === null
      )
    ) {
      return "Offsets must be numbers with at most 2 decimals";
    }
    return null;
  };

  const createInstance = () => {
    const validationError = validateHours();
    if (validationError) {
      toast.error(validationError);
      return;
    }
    const obj = {
      trial: trialId,
      notification_type: noti,
      hours_before: beforeHours,
      hours_after: afterType === "Non-Recurrent" ? afterHours : [],
      is_sms: isSms,
      is_app: isApp,
      is_email: isEmail,
      custom_title: title,
      custom_data: customData,
      custom_title_de: showDe ? customTitleDe : null,
      custom_data_de: showDe ? customDataDe : null,
      custom_title_es: showEs ? customTitleEs : null,
      custom_data_es: showEs ? customDataEs : null,
      after_visit_frequency_hours: afterType === "Recurrent" ? afterRecurring : "",
      // only send the adverse window mode that is selected, since a 0 offset is a valid value
      start_date: useOffset ? "" : startDate,
      end_date: useOffset ? "" : endDate,
      frequency: frequency,
      start_date_offset: useOffset ? startDateOffset : null,
      end_date_offset: useOffset ? endDateOffset : null,
    };
    saveTrialNotification(obj);
    resetState();
    onClose();
  };

  const optionsObject = {
    TRIAL_VISIT_NOTIFICATION: "Trial Visit Notification",
    ADVERSE_EVENT_NOTIFICATION: "Adverse Event Notification",
    ...(hasEdiary ? { EDIARY_NOTIFICATION: "eDiary Notification" } : {}),
  };

  const saveTrialNotification = (data) => {
    axios
      .post(`${__API_URL__}/api/appointments/add_trial_notification/`, data, {
        headers: {
          Authorization: `Token ${token}`,
        },
      })
      .then(() => {
        getTrialsNotification([]);
        getAdverseNotification([]);
        getEdiaryNotification?.();
        toast.success("Notification Added Successfully");
      })
      .catch((error) => {
        if (error.response) {
          toast.error(error.response.data.message);
        }
      });
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Add Notifications
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField
            select
            label="Notification Type"
            size="small"
            value={noti}
            onChange={(e) => setNoti(e.target.value)}
            fullWidth
            SelectProps={{ SelectDisplayProps: { "data-testid": "notif-type-select-trigger" } }}
          >
            <MenuItem value="">Select Type</MenuItem>
            {Object.entries(optionsObject).map(([key, label]) => (
              <MenuItem value={key} key={key}>
                {label}
              </MenuItem>
            ))}
          </TextField>

          {noti !== "APPOINTMENT_REMINDER" && (
            <>
              <TextField
                label="Custom Title (English)"
                size="small"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                fullWidth
                required
              />
              <TextField
                label="Custom Data (English)"
                size="small"
                value={customData}
                onChange={(e) => setCustomData(e.target.value)}
                multiline
                rows={3}
                fullWidth
                required
              />

              <Typography variant="subtitle2">Additional Languages</Typography>
              <Stack direction="row" spacing={2}>
                <FormControlLabel
                  control={<Checkbox checked={showDe} onChange={() => setShowDe(!showDe)} />}
                  label="German (DE)"
                />
                <FormControlLabel
                  control={<Checkbox checked={showEs} onChange={() => setShowEs(!showEs)} />}
                  label="Spanish (ES)"
                />
              </Stack>

              {showDe && (
                <>
                  <TextField
                    label="Custom Title (German)"
                    size="small"
                    value={customTitleDe}
                    onChange={(e) => setCustomTitleDe(e.target.value)}
                    fullWidth
                  />
                  <TextField
                    label="Custom Data (German)"
                    size="small"
                    value={customDataDe}
                    onChange={(e) => setCustomDataDe(e.target.value)}
                    multiline
                    rows={3}
                    fullWidth
                  />
                </>
              )}

              {showEs && (
                <>
                  <TextField
                    label="Custom Title (Spanish)"
                    size="small"
                    value={customTitleEs}
                    onChange={(e) => setCustomTitleEs(e.target.value)}
                    fullWidth
                  />
                  <TextField
                    label="Custom Data (Spanish)"
                    size="small"
                    value={customDataEs}
                    onChange={(e) => setCustomDataEs(e.target.value)}
                    multiline
                    rows={3}
                    fullWidth
                  />
                </>
              )}
            </>
          )}

          {noti !== "ADVERSE_EVENT_NOTIFICATION" && (
            <>
              <Typography variant="subtitle2">Notifications Before Visit</Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <TextField
                  type="number"
                  size="small"
                  placeholder="Enter hours"
                  label="Hours Before the Visit Start"
                  helperText={HOURS_HINT}
                  inputProps={{ min: 0.01, step: 0.01, "data-testid": "notif-before-hours-input" }}
                  value={beforeHourInput}
                  onChange={(e) => setBeforeHourInput(e.target.value)}
                  sx={{ flex: 1 }}
                />
                <Button
                  variant="contained"
                  size="small"
                  data-testid="notif-add-before-hours-btn"
                  onClick={handleAddBeforeHour}
                >
                  Add Hours
                </Button>
              </Stack>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {beforeHours.map((hour, index) => (
                  <Chip
                    key={index}
                    label={formatHoursDuration(hour)}
                    color="primary"
                    onDelete={() => removeBeforeHour(index)}
                  />
                ))}
              </Stack>

              <Divider />

              <Typography variant="subtitle2">Notifications During Visit</Typography>
              <RadioGroup row value={afterType} onChange={(e) => setAfterType(e.target.value)}>
                <FormControlLabel value="Non-Recurrent" control={<Radio />} label="Non-Recurrent" />
                <FormControlLabel value="Recurrent" control={<Radio />} label="Recurrent" />
              </RadioGroup>

              {afterType === "Non-Recurrent" ? (
                <>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <TextField
                      type="number"
                      size="small"
                      label="Hours After the Visit Start"
                      placeholder="Enter hours"
                      helperText={HOURS_HINT}
                      inputProps={{
                        min: 0.01,
                        step: 0.01,
                        "data-testid": "notif-after-hours-input",
                      }}
                      value={afterHourInput}
                      onChange={(e) => setAfterHourInput(e.target.value)}
                      sx={{ flex: 1 }}
                    />
                    <Button
                      variant="contained"
                      size="small"
                      data-testid="notif-add-after-hours-btn"
                      onClick={handleAddAfterHour}
                    >
                      Add Hours
                    </Button>
                  </Stack>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {afterHours.map((hour, index) => (
                      <Chip
                        key={index}
                        label={formatHoursDuration(hour)}
                        color="primary"
                        onDelete={() => removeAfterHour(index)}
                      />
                    ))}
                  </Stack>
                </>
              ) : (
                <Stack direction="row" spacing={1} alignItems="center">
                  <TextField
                    type="number"
                    size="small"
                    label="Send Notification (After Every X hours)"
                    placeholder="Enter hours"
                    helperText={HOURS_HINT}
                    inputProps={{
                      min: 0.01,
                      step: 0.01,
                      "data-testid": "notif-recurring-hours-input",
                    }}
                    value={afterRecurring}
                    onChange={(e) => setAfterRecurring(e.target.value)}
                    sx={{ flex: 1 }}
                  />
                  <Typography variant="body2">hours</Typography>
                </Stack>
              )}
            </>
          )}

          <Typography variant="subtitle2">Notification Channels</Typography>
          <Stack direction="row" spacing={2}>
            <FormControlLabel
              control={<Checkbox checked={isApp} onChange={() => setIsApp(!isApp)} />}
              label="In App"
            />
            <FormControlLabel
              control={<Checkbox checked={isSms} onChange={() => setIsSms(!isSms)} />}
              label="SMS"
            />
            <FormControlLabel
              control={<Checkbox checked={isEmail} onChange={() => setIsEmail(!isEmail)} />}
              label="Email"
            />
          </Stack>

          {noti === "ADVERSE_EVENT_NOTIFICATION" && (
            <>
              <Typography variant="subtitle2">Select One</Typography>
              <RadioGroup
                row
                value={useOffset ? "offset" : "date"}
                onChange={(e) => setUseOffset(e.target.value === "offset")}
              >
                <FormControlLabel value="offset" control={<Radio />} label="Use Offset" />
                <FormControlLabel value="date" control={<Radio />} label="Use Date" />
              </RadioGroup>

              {useOffset ? (
                <>
                  <TextField
                    type="number"
                    size="small"
                    label="Offset to the start time of Baseline Visit (in hours)"
                    helperText={HOURS_HINT}
                    inputProps={{ step: 0.01 }}
                    value={startDateOffset}
                    onChange={(e) => setStartDateOffset(e.target.value)}
                    fullWidth
                  />
                  <TextField
                    type="number"
                    size="small"
                    label="Offset to the end time of Last Visit (in hours)"
                    helperText={HOURS_HINT}
                    inputProps={{ step: 0.01 }}
                    value={endDateOffset}
                    onChange={(e) => setEndDateOffset(e.target.value)}
                    fullWidth
                  />
                </>
              ) : (
                <>
                  <TextField
                    type="date"
                    size="small"
                    label="Start Date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                  <TextField
                    type="date"
                    size="small"
                    label="End Date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                </>
              )}

              <TextField
                type="number"
                size="small"
                label="Frequency (in hours)"
                helperText={HOURS_HINT}
                value={frequency}
                onChange={(e) => setFrequency(e.target.value)}
                inputProps={{ min: 0.01, step: 0.01 }}
                fullWidth
              />
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={handleClose}>
          Close
        </Button>
        <Button variant="contained" data-testid="notif-send-btn" onClick={createInstance}>
          Send
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddTrialNotification;
